import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { resolvePublicFlagState } from "@/lib/feature-flags.access";

export type FeatureFlag = {
  key: string;
  enabled: boolean;
  label: string;
  description: string | null;
  updated_at: string;
};

function createPublishableClient() {
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  return createClient<Database>(process.env.SUPABASE_URL!, key, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) {
          h.delete("Authorization");
        }
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

/**
 * Ticket AD1a: enger Weg. Gibt ausschließlich den Zustand genau eines Flags
 * zurück und keine weiteren Namen. Bewusst ohne Anmeldepflicht, weil damit die
 * Sichtbarkeit öffentlicher Elemente gesteuert wird.
 */
export const getPublicFeatureFlag = createServerFn({ method: "GET" })
  .inputValidator((input: { key: string }) =>
    z.object({ key: z.string().min(1).max(100) }).parse(input),
  )
  .handler(async ({ data }): Promise<{ enabled: boolean }> => {
    const supabase = createPublishableClient();
    // Gelesen wird mit öffentlichen Rechten: die Leseregel der Datenbank gibt
    // nur Zeilen mit public_read = true heraus. Die zweite Bedingung (Name in
    // der Liste im Code) prüft resolvePublicFlagState — siehe dortige
    // Begründung, warum beide Bedingungen nötig sind.
    const { data: row, error } = await supabase
      .from("feature_flags")
      .select("enabled, public_read")
      .eq("key", data.key)
      .maybeSingle();
    if (error) throw new Error(error.message);
    const resolved = resolvePublicFlagState(data.key, row ?? null);
    if (!resolved) throw new Error("Forbidden");
    return resolved;
  });

/**
 * Ticket AD5: Zustand genau eines Flags für den internen Bereich. Nötig, weil
 * der Reiter „TTPA-Hinweisseite" am Flag hängt, dieses aber bewusst nicht
 * öffentlich lesbar ist. Lesen dürfen Administratoren und Redaktion.
 */
export const getInternalFeatureFlag = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { key: string }) =>
    z.object({ key: z.string().min(1).max(100) }).parse(input),
  )
  .handler(async ({ data, context }): Promise<{ enabled: boolean }> => {
    const { assertStaff } = await import("@/lib/landing-hundert-tage.access.server");
    await assertStaff(context.supabase, context.userId);
    const { data: row, error } = await context.supabase
      .from("feature_flags")
      .select("enabled")
      .eq("key", data.key)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return { enabled: row?.enabled === true };
  });

export const internalFeatureFlagQueryOptions = (key: string) =>
  queryOptions({
    queryKey: ["feature-flag", "internal", key],
    queryFn: () => getInternalFeatureFlag({ data: { key } }),
    staleTime: 60_000,
    retry: false,
  });



export const getAllFeatureFlags = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<FeatureFlag[]> => {
    // Ticket AD1a: Die vollständige Liste ist ausschließlich für
    // Administratoren lesbar. Der Schutz hängt damit nicht mehr allein an der
    // Leseregel der Datenbank.
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");

    const { data, error } = await context.supabase
      .from("feature_flags")
      .select("key, enabled, label, description, updated_at")
      .order("label", { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []) as FeatureFlag[];
  });


export const setFeatureFlag = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { key: string; enabled: boolean }) =>
    z.object({ key: z.string().min(1).max(100), enabled: z.boolean() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");

    // Ticket SK2: Die Kaufstrecke darf nur eingeschaltet werden, wenn die
    // Zugangsdaten der aktiven Betriebsart tragfähig sind. Sonst wollen Kundinnen
    // und Kunden bezahlen und nichts funktioniert — der gefährlichste Fehler
    // dieser Strecke. Geprüft wird beim Umschalten, nicht erst beim ersten Kauf.
    if (data.key === "checkout_stripe_enabled" && data.enabled) {
      const { stripeReadiness } = await import("@/lib/stripe.server");
      const readiness = stripeReadiness();
      if (!readiness.activeReady) {
        throw new Error(
          readiness.activeMessage ??
            "Die Zugangsdaten des Zahlungsdienstes sind unvollständig. Die Kaufstrecke bleibt geschlossen.",
        );
      }
    }

    const { error } = await context.supabase
      .from("feature_flags")
      .update({ enabled: data.enabled, updated_by: context.userId })
      .eq("key", data.key);
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });

export const publicFeatureFlagQueryOptions = (key: string) =>
  queryOptions({
    queryKey: ["feature-flag", key],
    queryFn: () => getPublicFeatureFlag({ data: { key } }),
    staleTime: 30_000,
    retry: false,
  });

export const allFeatureFlagsQueryOptions = () =>
  queryOptions({
    queryKey: ["feature-flags", "all"],
    queryFn: () => getAllFeatureFlags(),
    staleTime: 30_000,
  });
